import { EpiContent, GenericContentItem, LoadingStatus } from '@api/types';
import { BackgroundCard, Html, Image, LoadingSkeleton, Title } from '@components';
import { Box, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import classnames from 'classnames';

// STYLES
//--------
const USPListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${breakpoints.up('md')} {
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(3, minmax(0, 1fr));

    &.grid-4 {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`;

const USPItemWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 0;
  gap: 20px;
  text-align: center;
  padding: 16px;
  ${breakpoints.up('md')} {
    height: 100%;
    padding: 32px;
  }
  ${breakpoints.up('lg')} {
    padding: 48px;
  }
  &.compact {
    ${breakpoints.up('md')} {
      padding: 16px;
    }
    ${breakpoints.up('lg')} {
      padding: 24px;
    }
  }
`;

const ImageWrapper = styled(Box)`
  width: 100%;
  height: 0;
  padding-top: 66%;
  position: relative;
  ${breakpoints.up('md')} {
  }

  .epi-image,
  .MuiSkeleton-root {
    width: 100%;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const ContentWrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${breakpoints.up('md')} {
    gap: 16px;
    justify-content: center;
    align-items: stretch;
  }
`;

const TitleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${breakpoints.up('md')} {
    height: 64px;
  }
  ${breakpoints.up('lg')} {
    height: 96px;
  }
  .main-title {
    font-weight: 400;
  }
`;

// TYPES
//-------

export type USPListProps = {
  children?: React.ReactNode;
  content?: EpiContent;
  usp?: Array<GenericContentItem>;
  itemCount?: number;
};

export type USPItemProps = GenericContentItem &
  LoadingStatus & {
    children?: React.ReactNode;
    itemCount?: number;
  };

// COMPONENTS
//------------
export const USPList = ({ children = [], usp, itemCount = 1 }: USPListProps) => {
  let gridClassName = '';
  if (itemCount === 2) {
    gridClassName += ' grid-2';
  } else if (itemCount % 3 === 0) {
    gridClassName += ' grid-3';
  } else if (itemCount % 4 === 0) {
    gridClassName += ' grid-4';
  }

  const mainClassName = classnames('usp-list', gridClassName);
  return (
    <USPListWrapper className={mainClassName}>
      {children}
      {usp?.map((x, key) => (
        <USPItem {...x} key={key} itemCount={itemCount} />
      ))}
    </USPListWrapper>
  );
};

export const USPItem = ({
  image,
  title,
  text,
  isLoading,
  cardBackgroundColor,
  imageConfig,
  itemCount = 1,
  textColor,
  textAlign = 'center',
}: USPItemProps) => {
  const compact = itemCount % 4 === 0;
  const mainClassName = classnames('usp-item', { compact: compact });
  const titleSize = compact ? 'h5' : 'h3';
  const pictureSources = [
    { media: '(max-width:600px)', params: { width: 400, quality: 60, format: 'webp' } } as const,
    { media: '(max-width:960px)', params: { width: 500, quality: 70, format: 'webp' } } as const,
    { params: { width: 600, quality: 85, format: 'webp' } } as const,
  ];
  const imgSizes = '(max-width:600px) 100vw, (max-width:960px) 50vw, 410px';
  return (
    <BackgroundCard background={cardBackgroundColor}>
      <USPItemWrapper className={mainClassName}>
        <ImageWrapper>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <Image
              image={image}
              {...imageConfig}
              width='410'
              height='410'
              pictureSources={pictureSources}
              sizes={imgSizes}
            />
          )}
        </ImageWrapper>
        <ContentWrapper>
          <TitleWrapper>
            <Title
              variant={titleSize}
              className='no-dot line-clamp-2 wrap'
              noDot
              color={textColor}
              sx={{ textAlign: textAlign || 'center' }}
            >
              {isLoading ? <LoadingSkeleton width='100%' /> : title}
            </Title>
          </TitleWrapper>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <Box className='description wrap'>
              <Html
                html={text}
                color={textColor}
                sx={{
                  '& *': {
                    textAlign: `${textAlign || 'center'} !important`,
                  },
                }}
              />
            </Box>
          )}
        </ContentWrapper>
      </USPItemWrapper>
    </BackgroundCard>
  );
};
